<template>
    <DatatablePageLayout>
        <template v-slot:actionButtons>
            <button class="sk-btn sk-btn-primary rounded-lg" @click="handleCreate">
                {{ $t('apps.campaigns.form.createButton') }}
            </button>
        </template>
        <template v-slot:additional>
            <div class="mb-12">
                <h4 class="mb-4">{{ $t('admin.photos.filters') }}</h4>
                <Combobox
                    v-model="applicationScopeId"
                    class="my-4 w-1/3"
                    :multiple="false"
                    :cleareable="true"
                    :options="scopes.filter(scope => scope.scope_type.name === 'application').map(scope => ({ key: scope.id, text: scope.name }))"
                    :label="$t('dashboard.app')"
                />

                <Combobox
                    v-model="resortScopeId"
                    class="my-4 w-1/3"
                    :multiple="false"
                    :cleareable="true"
                    :options="scopes.filter(scope => scope.scope_type.name === 'resort').map(scope => ({ key: scope.id, text: scope.name }))"
                    :label="$t('dashboard.resort')"
                />
            </div>

        </template>
        <template v-slot:datatable>
            <DataTable
                :config="datatableConfig"
                :elements="users"
                :stripe="true"
                :draggable="false"
                :sst="true"
                :pagination="pagination"
                @filter="handleFilter"
                @action="handleAction"
            />
        </template>
    </DatatablePageLayout>
</template>

<script>

import DataTable from '@/modules/Shared/Components/table/SKDataTable.vue'
import loader from "@/modules/Shared/Mixins/loader";
import PaginationService from "@/modules/Shared/Services/PaginationService";
import UsersService from "@/modules/Admin/Services/UsersService";
import DatatablePageLayout from "@/modules/Shared/Pages/DatatablePageLayout.vue";
import {unifyPaginatedResponse} from "@/modules/Shared/Helpers/apiResponseHelper";
import AuthorizationService from "@/modules/Admin/Services/AuthorizationService";
import Combobox from "@/modules/Shared/Components/Combobox.vue";

export default {
  name: "UsersPage",
  components: {Combobox, DatatablePageLayout, DataTable},
  mixins: [loader],
  props: {
    withCredential: Boolean,
  },
  data() {
    return {
      users: [],
      scopes: [],
      applicationScopeId: null,
      resortScopeId: null,
      userIds: [],
      defaultFilters: [],
      typeOptions: [
          { value: 'editor', label: 'Editor' },
          { value: 'admin', label: 'Admin' }
      ],
      datatableConfig: [],
      pagination: {},
      presetFilters: [
          { field: 'type', value: 'editor;admin' },
          { field: 'credential', value: this.withCredential ? '1' : '0' }
      ]
    }
  },
  async created() {
      this.datatableConfig = this.getDatatableConfig()

      this.scopes = await unifyPaginatedResponse(AuthorizationService.getScopes, PaginationService.parseParameters({
          per_page: 200,
          filters: []
      }))

      this.tabIndex = this.$route.params.scopeTypeId
          ? this.$route.params.scopeTypeId - 1
          : 0

      await this.loadInitialData()
  },
  watch: {
      applicationScopeId: {
          handler: function (value) {
              if (value) {
                  this.resortScopeId = null
                  this.getScopeUsers(value)
              } else {
                  this.userIds = []
                  this.loadInitialData()
              }
          }
      },
      resortScopeId: {
          handler: function (value) {
              if (value) {
                  this.applicationScopeId = null
                  this.getScopeUsers(value)
              } else {
                  this.userIds = []
                  this.loadInitialData()
              }
          }
      }
  },
  methods: {
      getTypeConfig() {
          return { 
            field: 'type',
            head: this.$t('apps.bannerspaces.form.typeinput'),
            searchable: true,
            searchableoptions: [
                { value: 'editor', label: 'Editor' },
                { value: 'admin', label: 'Admin' }
            ],
            options: [
                { value: 'editor', label: 'Editor' },
                { value: 'admin', label: 'Admin' }
            ],
            sortable : false
          }
      },
      getDatatableConfig() {
          if (this.withCredential) {
            return [
              { field: 'username', head : this.$t('apps.registered-users.username'), searchable: true, sortable: false },
              { field: 'credential_internal_owner', head: this.$t('admin.shared-credentials.list.internal-owner'), searchable: false, sortable: false },
              this.getTypeConfig(),
              { field: 'status', head: this.$t('admin.photos.status'), type: 'html', searchable: false, sortable: false },
              { field: 'actions', type: 'actions', head: this.$t("geofences.geofences-messages.actions"), searchable: false, sortable: false }
            ]
          }

          return [
              { field: 'username', head : this.$t('apps.registered-users.username'), searchable: true, sortable: false },
              { field: 'email', head : this.$t('apps.registered-users.email'), searchable: true, sortable: false },
              this.getTypeConfig(),
              { field: 'actions', type: 'actions', head: this.$t("geofences.geofences-messages.actions"), searchable: false, sortable: false }
          ]
      },
      async loadInitialData() {
          await this.getUsers({
              filters: this.presetFilters,
              page: 1
          })
      },
      async getScopeUsers(scopeId) {
          const usersResponse = await unifyPaginatedResponse(AuthorizationService.getUsers, PaginationService.parseParameters({
              per_page: 30,
              filters: [
                  { field: 'scope_id', value: scopeId }
              ]
          }))

          this.userIds = usersResponse.map(user => user.id)

          await this.getUsers({
              filters: [
                  { field: 'id_list', value: this.userIds.join(';') }
              ]
          })
      },
      async getUsers(params) {
          await this.load(async () => {
              const users = await UsersService.getUsers(PaginationService.parseParameters(params))
              this.users =  this.parseUsers(users.data)
              this.pagination = {
                  per_page: users.per_page,
                  total: users.total
              }
          })
      },
      async handleFilter(params) {
          if (!params.filters.find(filter => filter.field === 'type')) {
              params.filters.push({ field: 'type', value: 'editor;admin' })
          }

          if (this.userIds.length > 0) {
              params.filters.push({ field: 'id_list', value: this.userIds.join(';') })
          }

          await this.getUsers(
              {
                  filters: [...params.filters, { field: 'credential', value: this.withCredential ? '1' : '0' }],
                  page: params.page
              }
          )
      },
      handleCreate() {
          this.$router.push({
              name: this.withCredential ? 'admin-shared-credential-create' : 'admin-user-create',
              params: {
                  scopeType: this.scopeType
              }
          });
      },
      async handleAction(type, uuid){
          switch(type) {
              case 'edit':
                  await this.$router.push({
                      name: this.withCredential ? 'admin-shared-credential-edit' : 'admin-user-edit',
                      params: {
                          'uuid': uuid,
                      }
                  });
                  break;
              case 'delete':
                  await this.deleteUser(uuid)
                  break;
          }
      },
      async deleteUser(uuid) {
          await this.loadAndNotify(async () => {
              await UsersService.deleteUser(uuid)
              await this.getUsers({
                  filters: this.presetFilters,
                  page: 1
              })
          })
      },
      parseUsers(users) {
          return users.map(user => ({
              ...user,
              status: this.setStatus(user.enabled_credential),
              actions: this.withCredential ? [ 'edit' ] : [ 'edit', 'delete' ]
          }))
      },
      setStatus(enabled) {
          if (enabled) {
              return `<div class="capitalize border border-success text-success py-2 px-4 rounded-full">${this.$t('admin.apps.status.enabled')}</div>`
          }

          return `<div class="capitalize border border-danger text-danger py-2 px-4 rounded-full">${this.$t('admin.apps.status.disabled')}</div>`
      },
  }
};
</script>
